import React from "react";
import { SkipBack, SkipForward, Shuffle, Repeat, RepeatOnce, FloppyDisk, CheckCircle, ShareNetwork, Fire } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Compact transport-control strip used inside AudioVisualizerPlayer and
// InAppVideoPlayer. Reuses the iconography users expect from Spotify /
// YouTube Music so no labels are required even at 11px.
//
// Props:
//   shuffleOn   – boolean
//   loopMode    – "off" | "all" | "one"
//   onShuffle   – toggle shuffle
//   onLoop      – cycle loop mode
//   onPrev      – jump to previous track
//   onNext      – jump to next track
//   canPrev     – boolean
//   canNext     – boolean
//   accent      – tailwind color name
//   testidPrefix – allows the two players to keep distinct test IDs
//   bucket      – when set + user logged in + queue has items, a "Save
//                 queue as playlist" disk icon appears. Tap → prompts
//                 for a name → POSTs to /api/playlists/my/from-queue.
//   queue       – array of {id, title, ...} currently visible in player
//   shareItem   – { title, subtitle?, id? } of the *currently playing*
//                 song/video/movie. When provided, a Share button
//                 appears in the transport strip. Uses Web Share API on
//                 mobile (native share sheet → texts, WhatsApp, etc.)
//                 and falls back to clipboard on desktop.
export default function PlayerControls({
  shuffleOn = false,
  loopMode = "all",
  onShuffle,
  onLoop,
  onPrev,
  onNext,
  canPrev = true,
  canNext = true,
  accent = "terracotta",
  testidPrefix = "player",
  bucket = null,
  queue = null,
  shareItem = null,
}) {
  const { user } = useAuth();
  const [saving, setSaving] = React.useState(false);
  const [savedId, setSavedId] = React.useState(null);
  const [justShared, setJustShared] = React.useState(false);
  const [justPinned, setJustPinned] = React.useState(false);
  // Two-tap Pin recording state. Tap 1 starts, tap 2 (or 30s) stops.
  const [isRecording, setIsRecording] = React.useState(false);
  const [recElapsed, setRecElapsed] = React.useState(0); // whole seconds shown live
  const recRef = React.useRef({ playStart: 0, wallStart: 0, timer: null });
  const MAX_CLIP_SEC = 30;
  const canSave = !!bucket && Array.isArray(queue) && queue.length > 1 && !!user;
  const canShare = !!shareItem && !!shareItem.title;

  // Build the share URL. We deep-link back to the current page with
  // ?play=<id> so the recipient lands on the exact track / video /
  // movie auto-opened — same convention as the History page.
  const buildShareUrl = (t) => {
    if (typeof window === "undefined") return "";
    const { origin, pathname } = window.location;
    if (shareItem?.id) {
      const base = `${origin}${pathname}?play=${encodeURIComponent(shareItem.id)}`;
      return t > 0 ? `${base}&t=${t}` : base;
    }
    return window.location.href;
  };

  // Current playback second (when the player exposes it), floored.
  const currentMomentSec = () => Math.max(
    0,
    Math.floor(
      typeof shareItem?.getCurrentTime === "function"
        ? (shareItem.getCurrentTime() || 0)
        : 0
    )
  );

  // Log a moment to the Discover · Trending leaderboard (fire-and-forget).
  // Section falls back to the current route slug so the deep link reopens
  // in the right section and jumps to the saved second. `duration` is the
  // recorded clip length (1..30s) captured by the two-tap Pin flow.
  const logMomentToTrending = async (t, duration) => {
    if (!shareItem?.id) return;
    try {
      const section = (
        shareItem.section ||
        (typeof window !== "undefined"
          ? (window.location.pathname.split("/").filter(Boolean)[0] || "sermons")
          : "sermons")
      ).toLowerCase();
      await api.post("/discover/share", {
        item_id: shareItem.id,
        item_title: shareItem.title,
        media_url: shareItem.mediaUrl || `hw:item:${shareItem.id}`,
        start_sec: t,
        section,
        snippet: "",
        ...(duration ? { duration } : {}),
      });
    } catch (_) { /* telemetry only — board ranking is best-effort */ }
  };

  // Clean up the recording timer on unmount.
  React.useEffect(() => () => {
    if (recRef.current.timer) clearInterval(recRef.current.timer);
  }, []);

  // Finalize a recording: compute the clip length and pin it.
  const stopRecording = React.useCallback(async () => {
    const r = recRef.current;
    if (r.timer) { clearInterval(r.timer); r.timer = null; }
    const nowPlay = currentMomentSec();
    const playDelta = nowPlay - r.playStart;
    const wallDelta = (Date.now() - r.wallStart) / 1000;
    // Prefer playback delta (true content length); fall back to wall clock
    // when the player can't report time. Clamp into the 1..30s window.
    let duration = playDelta > 0 ? playDelta : wallDelta;
    duration = Math.max(1, Math.min(Math.round(duration), MAX_CLIP_SEC));
    setIsRecording(false);
    await logMomentToTrending(r.playStart, duration);
    setJustPinned(true);
    setTimeout(() => setJustPinned(false), 2500);
    const mm = Math.floor(r.playStart / 60);
    const ss = String(r.playStart % 60).padStart(2, "0");
    toast.success("Pinned to Trending", {
      description: `Saved a ${duration}s clip starting at ${mm}:${ss}`,
    });
  }, [shareItem]);

  // Two-tap "Pin to Trending":
  //   Tap 1 → start recording (marks the start second), live counter runs.
  //   Tap 2 → stop & save the exact clip (auto-stops at 30s max).
  const handlePinToTrending = async () => {
    if (!canShare) return;
    if (isRecording) { await stopRecording(); return; }
    const start = currentMomentSec();
    recRef.current.playStart = start;
    recRef.current.wallStart = Date.now();
    setRecElapsed(0);
    setIsRecording(true);
    recRef.current.timer = setInterval(() => {
      const elapsed = Math.round((Date.now() - recRef.current.wallStart) / 1000);
      setRecElapsed(elapsed);
      if (elapsed >= MAX_CLIP_SEC) { stopRecording(); }
    }, 250);
  };

  const handleShare = async () => {
    if (!canShare) return;
    // Grab the exact playback second when the player exposes it, so the
    // share becomes a "moment" that can rank on the Discover · Trending
    // board (works for sermons, songs, movies, shorts, lives).
    const t = currentMomentSec();
    const url = buildShareUrl(t);
    const title = `${shareItem.title} · Holy Words`;
    const text = t > 0
      ? `Listen to this moment in "${shareItem.title}" on Holy Words`
      : (shareItem.subtitle
          ? `Listening to "${shareItem.title}" — ${shareItem.subtitle} on Holy Words`
          : `Check out "${shareItem.title}" on Holy Words`);

    // Sharing also pins the moment to the Trending board.
    await logMomentToTrending(t);

    const data = { title, text, url };
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(data);
        setJustShared(true);
        setTimeout(() => setJustShared(false), 2500);
        return;
      } catch (_) {
        // User cancelled the share sheet — silent, no toast.
        return;
      }
    }
    // Desktop / unsupported — copy a tidy share blurb to clipboard.
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success(t > 0 ? "Moment link copied — paste it anywhere" : "Link copied — paste it anywhere");
        setJustShared(true);
        setTimeout(() => setJustShared(false), 2500);
      }
    } catch (_) {
      toast.error("Couldn't copy the link");
    }
  };

  const handleSaveQueue = async () => {
    if (!canSave || saving) return;
    const defaultName = `My ${bucket.replace(/_/g, " ")} mix · ${new Date().toLocaleDateString()}`;
    const name = typeof window !== "undefined"
      ? window.prompt("Name this playlist:", defaultName)
      : defaultName;
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      const items = queue.map((t) => ({ bucket, track_id: t.id }));
      const res = await api.post("/playlists/my/from-queue", {
        title: name.trim(),
        accent,
        items,
      });
      setSavedId(res?.data?.id || true);
      setTimeout(() => setSavedId(null), 3500);
    } catch (e) {
      // Fail soft — bring the prompt back with a hint.
      const msg = e?.response?.data?.detail || "Couldn't save right now. Try again?";
      if (typeof window !== "undefined") window.alert(msg);
    } finally {
      setSaving(false);
    }
  };
  const Btn = ({ active, disabled, onClick, "aria-label": label, "data-testid": tid, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-testid={tid}
      aria-pressed={active ? "true" : "false"}
      className={[
        "w-10 h-10 grid place-items-center rounded-full transition-all",
        disabled ? "opacity-30 cursor-not-allowed" : "hover:scale-110",
        active ? `bg-${accent} text-cream shadow-md` : "bg-cream/10 text-cream hover:bg-cream/20",
      ].join(" ")}
    >
      {children}
    </button>
  );

  return (
    <div
      className="mt-4 flex items-center gap-2"
      data-testid={`${testidPrefix}-controls`}
    >
      <Btn
        onClick={onShuffle}
        active={shuffleOn}
        aria-label={shuffleOn ? "Disable shuffle" : "Enable shuffle"}
        data-testid={`${testidPrefix}-shuffle`}
      >
        <Shuffle size={14} weight="bold" />
      </Btn>
      <Btn
        onClick={onPrev}
        disabled={!canPrev}
        aria-label="Previous track"
        data-testid={`${testidPrefix}-prev`}
      >
        <SkipBack size={14} weight="fill" />
      </Btn>
      <Btn
        onClick={onNext}
        disabled={!canNext}
        aria-label="Next track"
        data-testid={`${testidPrefix}-next`}
      >
        <SkipForward size={14} weight="fill" />
      </Btn>
      <Btn
        onClick={onLoop}
        active={loopMode !== "off"}
        aria-label={`Loop ${loopMode}`}
        data-testid={`${testidPrefix}-loop`}
      >
        {loopMode === "one" ? <RepeatOnce size={14} weight="bold" /> : <Repeat size={14} weight="bold" />}
      </Btn>
      {canSave && (
        <Btn
          onClick={handleSaveQueue}
          disabled={saving}
          active={!!savedId}
          aria-label="Save queue as playlist"
          data-testid={`${testidPrefix}-save-queue`}
        >
          {savedId ? <CheckCircle size={14} weight="fill" /> : <FloppyDisk size={14} weight="fill" />}
        </Btn>
      )}
      {canShare && (
        <button
          type="button"
          onClick={handlePinToTrending}
          aria-label={isRecording ? "Stop recording and pin this clip" : `Start a clip of ${shareItem.title} to pin to Trending`}
          aria-pressed={isRecording ? "true" : "false"}
          title={isRecording ? "Tap to stop & pin (max 30s)" : "Tap to start a clip, tap again to pin it (max 30s)"}
          data-testid={`${testidPrefix}-pin-trending`}
          className={`inline-flex items-center gap-1.5 px-1 h-9 text-[11px] tracking-[0.18em] uppercase font-bold transition-all ${
            isRecording ? "text-red-400 animate-pulse" : justPinned ? "text-amber-300" : "text-cream/85 hover:text-cream"
          }`}
        >
          {isRecording
            ? <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
            : justPinned ? <CheckCircle size={14} weight="fill" /> : <Fire size={14} weight="fill" />}
          <span data-testid={`${testidPrefix}-pin-label`}>
            {isRecording ? `Stop ${recElapsed}s` : justPinned ? "Pinned" : "Pin"}
          </span>
        </button>
      )}
      {canShare && (
        <button
          type="button"
          onClick={handleShare}
          aria-label={`Share ${shareItem.title}`}
          data-testid={`${testidPrefix}-share`}
          className={`inline-flex items-center gap-1.5 px-1 h-9 text-[11px] tracking-[0.18em] uppercase font-bold transition-all ${justShared ? "text-amber-300" : "text-cream/85 hover:text-cream"}`}
        >
          {justShared ? <CheckCircle size={14} weight="fill" /> : <ShareNetwork size={14} weight="bold" />}
          <span>{justShared ? "Shared" : "Share"}</span>
        </button>
      )}
      <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-cream/55 font-semibold">
        {isRecording
          ? `Recording clip… ${recElapsed}s / ${MAX_CLIP_SEC}s`
          : justPinned
          ? "Pinned to Trending!"
          : justShared
          ? "Shared!"
          : savedId
          ? "Saved to My Playlists"
          : loopMode === "one"
          ? "Repeat track"
          : shuffleOn
          ? "Shuffle · Continuous"
          : "Continuous"}
      </span>
    </div>
  );
}
